import { AnimatePresence, motion } from 'framer-motion'
import { RefreshCw, Sparkles, X } from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import { useUpdateAvailable } from './update-context'

/**
 * Centre-screen "new version ready" banner. Non-blocking (no backdrop — the
 * rest of the page stays interactive) and never auto-reloads. "Later" hides it
 * but the persistent sidebar entry remains, so the update is always reachable.
 */
export function UpdateAvailableBanner() {
  const {
    updateAvailable,
    bannerDismissed,
    dismissBanner,
    applyUpdate,
    applying,
  } = useUpdateAvailable()
  const open = updateAvailable && !bannerDismissed

  return (
    <AnimatePresence>
      {open ? (
        <div className="pointer-events-none fixed inset-0 z-[300] flex items-center justify-center p-4">
          <motion.div
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="pointer-events-auto w-full max-w-sm"
          >
            <div className="new-ui-theme flex items-start gap-3 rounded-xl border border-border bg-popover p-4">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-info/10 text-info">
                <Sparkles className="size-[18px]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  New update available
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  We've shipped improvements — refresh to get the latest
                  version.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={applying}
                    onClick={applyUpdate}
                    leftIcon={
                      <RefreshCw
                        className={applying ? 'animate-spin' : undefined}
                      />
                    }
                  >
                    {applying ? 'Updating…' : 'Refresh'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={dismissBanner}>
                    Later
                  </Button>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Dismiss"
                onClick={dismissBanner}
              >
                <X className="size-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  )
}

export default UpdateAvailableBanner
